import React, { useState, useEffect, useCallback, useRef } from 'react';
import { resourcesApi, getFileUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Bookmark, Upload, Trash2, ExternalLink, FileText, Plus, X, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ResourcesPanel() {
  const [bookmarks, setBookmarks] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showAddBookmark, setShowAddBookmark] = useState(false);
  const [bookmarkTitle, setBookmarkTitle] = useState('');
  const [bookmarkUrl, setBookmarkUrl] = useState('');
  const fileInputRef = useRef(null);

  const fetchResources = useCallback(async () => {
    try {
      const res = await resourcesApi.getAll();
      setBookmarks(res.data.bookmarks);
      setFiles(res.data.files);
    } catch (err) { void err; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const handleAddBookmark = async (e) => {
    e.preventDefault();
    if (!bookmarkTitle.trim() || !bookmarkUrl.trim()) return;
    try {
      const res = await resourcesApi.createBookmark({ title: bookmarkTitle, url: bookmarkUrl });
      setBookmarks(prev => [...prev, res.data]);
      setBookmarkTitle('');
      setBookmarkUrl('');
      setShowAddBookmark(false);
      toast.success('Bookmark saved');
    } catch (err) { toast.error('Failed to save bookmark'); void err; }
  };

  const handleDeleteBookmark = async (id) => {
    try {
      await resourcesApi.deleteBookmark(id);
      setBookmarks(prev => prev.filter(b => b.id !== id));
      toast.success('Bookmark removed');
    } catch (err) { toast.error('Failed to remove'); void err; }
  };

  const handleUploadFile = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const f of fileList) {
        const res = await resourcesApi.uploadFile(f);
        setFiles(prev => [...prev, res.data]);
      }
      toast.success('File(s) uploaded');
    } catch (err) { toast.error('Upload failed'); void err; }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (id) => {
    try {
      await resourcesApi.deleteFile(id);
      setFiles(prev => prev.filter(f => f.id !== id));
      toast.success('File removed');
    } catch (err) { toast.error('Failed to remove'); void err; }
  };

  const handleDownloadFile = async (file) => {
    try {
      const url = getFileUrl(file.storage_path);
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) { toast.error('Download failed'); void err; }
  };

  if (loading) {
    return (
      <div className="bg-[#23272E] border border-[#33383F] rounded-2xl p-4 h-full flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#4A9D6E]" />
      </div>
    );
  }

  return (
    <div className="bg-[#23272E] border border-[#33383F] rounded-2xl p-4 h-full" data-testid="resources-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bookmark className="w-4 h-4 text-[#4A9D6E]" />
          <h3 className="font-bold text-[#F0EDE8] tracking-tight">Resources</h3>
        </div>
        <Badge variant="secondary" className="bg-[#2C3038] text-[#9CA3AF] rounded-xl">
          {bookmarks.length + files.length}
        </Badge>
      </div>

      <ScrollArea className="h-[280px]">
        <div className="space-y-3">
          {/* Bookmarks */}
          {bookmarks.length > 0 && (
            <div>
              <p className="accent-text text-[#9CA3AF] mb-1.5">BOOKMARKS</p>
              <div className="space-y-1">
                {bookmarks.map(b => (
                  <div key={b.id} className="flex items-center gap-2 py-1.5 group" data-testid={`bookmark-${b.id}`}>
                    <ExternalLink className="w-3.5 h-3.5 text-[#4A9D6E] flex-shrink-0" />
                    <a href={b.url} target="_blank" rel="noopener noreferrer"
                      className="flex-1 text-sm text-[#F0EDE8] hover:text-[#4A9D6E] truncate transition-colors">
                      {b.title}
                    </a>
                    <Button size="icon" variant="ghost"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 text-[#E55B5B] hover:text-[#E55B5B] hover:bg-[#E55B5B]/10 rounded-lg"
                      onClick={() => handleDeleteBookmark(b.id)} data-testid={`delete-bookmark-${b.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Files */}
          {files.length > 0 && (
            <div>
              <p className="accent-text text-[#9CA3AF] mb-1.5">FILES</p>
              <div className="space-y-1">
                {files.map(f => (
                  <div key={f.id} className="flex items-center gap-2 py-1.5 group" data-testid={`resource-file-${f.id}`}>
                    <FileText className="w-3.5 h-3.5 text-[#D4855A] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#F0EDE8] truncate">{f.original_filename}</p>
                      <p className="text-xs text-[#555D68]">{formatFileSize(f.size)}</p>
                    </div>
                    <Button size="icon" variant="ghost"
                      className="h-6 w-6 text-[#9CA3AF] hover:text-[#4A9D6E] hover:bg-[#2C3038] rounded-lg"
                      onClick={() => handleDownloadFile(f)} data-testid={`download-resource-${f.id}`}>
                      <Download className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="ghost"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 text-[#E55B5B] hover:text-[#E55B5B] hover:bg-[#E55B5B]/10 rounded-lg"
                      onClick={() => handleDeleteFile(f.id)} data-testid={`delete-resource-file-${f.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bookmarks.length === 0 && files.length === 0 && (
            <div className="text-center py-6 text-[#555D68]">
              <Bookmark className="w-8 h-8 mx-auto mb-2 text-[#33383F]" />
              <p className="text-sm">No resources yet</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Actions */}
      <div className="mt-3 space-y-2">
        {showAddBookmark ? (
          <form onSubmit={handleAddBookmark} className="space-y-2">
            <Input value={bookmarkTitle} onChange={(e) => setBookmarkTitle(e.target.value)}
              placeholder="Title" autoFocus
              className="h-8 text-sm bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
              data-testid="bookmark-title-input" />
            <Input value={bookmarkUrl} onChange={(e) => setBookmarkUrl(e.target.value)}
              placeholder="https://..."
              className="h-8 text-sm bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
              data-testid="bookmark-url-input" />
            <div className="flex gap-2">
              <Button type="submit" size="sm" className="flex-1 h-8 bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl"
                data-testid="save-bookmark-button">Save</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddBookmark(false)}
                className="h-8 text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"><X className="w-3 h-3" /></Button>
            </div>
          </form>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowAddBookmark(true)}
              className="flex-1 h-8 border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl text-xs"
              data-testid="add-bookmark-button">
              <Plus className="w-3 h-3 mr-1" />Bookmark
            </Button>
            <input ref={fileInputRef} type="file" multiple onChange={handleUploadFile} className="hidden" data-testid="resource-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="flex-1 h-8 border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl text-xs"
              data-testid="upload-resource-button">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
              File
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
